import React, { useState } from 'react';
import { 
  Brain, 
  ChevronDown, 
  ChevronUp, 
  Lightbulb, 
  TrendingUp, 
  AlertTriangle, 
  Heart,
  Sparkles,
  Star,
  Zap,
  Target,
  Shield,
  Activity,
  BarChart3
} from 'lucide-react';

const JournalInsights = ({ entry, insights, onGenerateInsights, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getMoodIcon = (mood) => {
    const moodIcons = {
      'very-low': '😔',
      'low': '😕',
      'neutral': '😐',
      'good': '🙂',
      'excellent': '😊'
    };
    return moodIcons[mood] || '😐';
  };

  const getRiskLevelColor = (riskLevel) => {
    const colors = {
      'low': 'text-green-600 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200',
      'medium': 'text-yellow-600 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200',
      'high': 'text-red-600 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200'
    };
    return colors[riskLevel] || 'text-gray-600 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200';
  };

  const getRiskLevelIcon = (riskLevel) => {
    if (riskLevel === 'high') return <AlertTriangle className="w-4 h-4" />;
    if (riskLevel === 'medium') return <TrendingUp className="w-4 h-4" />;
    return <Heart className="w-4 h-4" />;
  };

  if (!insights && !loading) {
    return (
      <div className="mt-4 sm:mt-6 p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex-shrink-0">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-700">AI Insights</span>
          </div>
          <button
            onClick={() => onGenerateInsights(entry._id)}
            disabled={loading}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-xl font-medium hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-sm"
          >
            <Sparkles className="w-4 h-4" />
            <span>Generate Insights</span>
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-4 sm:mt-6 p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
        <div className="flex items-center justify-center space-x-3">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
          <span className="text-sm text-gray-600 font-medium">Generating AI insights...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 sm:mt-6 bg-gradient-to-br from-white to-blue-50 rounded-2xl border-2 border-blue-100 shadow-lg">
      {/* Header */}
      <div 
        className="p-4 sm:p-6 cursor-pointer hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 transition-all duration-200 rounded-t-2xl"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className="p-2 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex-shrink-0">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-bold text-gray-900 text-sm sm:text-base">AI Insights</span>
              <span className="text-xs text-gray-500 ml-2">• {insights.analysis.themes.length} themes identified</span>
            </div>
          </div>
          <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 flex-shrink-0">
            {isExpanded ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-blue-100 p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Main Analysis */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100">
            <h4 className="text-sm font-bold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
              <div className="p-1 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex-shrink-0">
                <Target className="w-3 h-3 text-white" />
              </div>
              <span>Analysis</span>
            </h4>
            <p className="text-sm text-gray-700 leading-relaxed">{insights.content}</p>
          </div>

          {/* Mood Assessment */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 sm:p-4 border border-green-100">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <span className="text-sm font-bold text-gray-900">Mood Assessment:</span>
              <span className="text-xl sm:text-2xl">{getMoodIcon(insights.analysis.mood)}</span>
              <span className="text-sm text-gray-600 capitalize font-medium">{insights.analysis.mood}</span>
            </div>
          </div>

          {/* Risk Level */}
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-3 sm:p-4 border border-yellow-100">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <span className="text-sm font-bold text-gray-900">Risk Level:</span>
              <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-xl text-xs font-semibold ${getRiskLevelColor(insights.analysis.riskLevel)}`}>
                {getRiskLevelIcon(insights.analysis.riskLevel)}
                <span className="ml-1 capitalize">{insights.analysis.riskLevel}</span>
              </span>
            </div>
          </div>

          {/* Themes */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100">
            <h4 className="text-sm font-bold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
              <div className="p-1 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex-shrink-0">
                <Lightbulb className="w-3 h-3 text-white" />
              </div>
              <span>Key Themes</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {insights.analysis.themes.map((theme, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 sm:px-3 py-1 rounded-xl text-xs bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 border border-blue-200 font-medium"
                >
                  <Lightbulb className="w-3 h-3 mr-1" />
                  {theme}
                </span>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-100">
            <h4 className="text-sm font-bold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
              <div className="p-1 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex-shrink-0">
                <Star className="w-3 h-3 text-white" />
              </div>
              <span>Recommendations</span>
            </h4>
            <ul className="space-y-2">
              {insights.analysis.recommendations.map((recommendation, index) => (
                <li key={index} className="text-sm text-gray-700 flex items-start space-x-2">
                  <div className="w-2 h-2 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Regenerate Button */}
          <div className="pt-3 sm:pt-4 border-t border-blue-100">
            <button
              onClick={() => onGenerateInsights(entry._id)}
              disabled={loading}
              className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-sm"
            >
              <Zap className="w-4 h-4" />
              <span>Regenerate Insights</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalInsights; 